import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Student } from "@/types";
import { format } from "date-fns";
import { Download, FileText } from "lucide-react";
import { Document, Packer, Paragraph, TextRun, Table, TableRow, TableCell, HeadingLevel, AlignmentType } from "docx";
import { saveAs } from "file-saver";
import { useToast } from "@/hooks/use-toast";

interface GenerateReportDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  student: Student;
  profile: any;
  readings: any[];
}

export function GenerateReportDialog({ 
  open, 
  onOpenChange, 
  student, 
  profile,
  readings 
}: GenerateReportDialogProps) {
  const { toast } = useToast();

  const generateDocument = async () => {
    try {
      const parameters = (profile.parameters as any[]) || [];

      // Calculate statistics
      const stats: Record<string, { min: number; max: number; avg: number; count: number }> = {};
      parameters.forEach(param => {
        const values = readings
          .map(r => (r.readings as any[])?.find((rd: any) => rd.name === param.name)?.value)
          .filter(v => v !== undefined);
        
        if (values.length > 0) {
          stats[param.name] = {
            min: Math.min(...values),
            max: Math.max(...values),
            avg: values.reduce((a, b) => a + b, 0) / values.length,
            count: values.length
          };
        }
      });

      // Count alerts
      const totalAlerts = readings.reduce((sum, r) => 
        sum + ((r.alerts_triggered as any[])?.length || 0), 0
      );

      const doc = new Document({
        sections: [{
          children: [
            new Paragraph({
              text: "Special Monitoring Report",
              heading: HeadingLevel.HEADING_1,
              alignment: AlignmentType.CENTER,
            }),
            new Paragraph({
              text: `Student: ${student.surname}, ${student.other_names}`,
              spacing: { before: 400, after: 200 },
            }),
            new Paragraph({
              text: `Class: ${student.class} ${student.stream}`,
            }),
            new Paragraph({
              text: `Report Period: ${format(new Date(profile.created_at), 'MMM dd, yyyy')} - ${format(new Date(), 'MMM dd, yyyy')}`,
            }),
            new Paragraph({
              text: `Total Readings: ${readings.length}`,
            }),
            new Paragraph({
              text: `Total Alerts: ${totalAlerts}`,
              spacing: { after: 400 },
            }),
            
            new Paragraph({
              text: "Summary Statistics",
              heading: HeadingLevel.HEADING_2,
              spacing: { before: 400, after: 200 },
            }),
            
            ...Object.entries(stats).flatMap(([param, stat]) => [
              new Paragraph({
                children: [
                  new TextRun({
                    text: `${param}:`,
                    bold: true,
                  }),
                ],
                spacing: { before: 200 },
              }),
              new Paragraph({
                text: `  Min: ${stat.min.toFixed(2)} | Max: ${stat.max.toFixed(2)} | Average: ${stat.avg.toFixed(2)} | Count: ${stat.count}`,
              }),
            ]),

            new Paragraph({
              text: "Reading History",
              heading: HeadingLevel.HEADING_2,
              spacing: { before: 400, after: 200 },
            }),

            ...readings.slice(0, 20).map(reading => 
              new Paragraph({
                children: [
                  new TextRun({
                    text: `${format(new Date(reading.recorded_at), 'MMM dd, yyyy HH:mm')}: `,
                    bold: true,
                  }),
                  new TextRun({
                    text: (reading.readings as any[])?.map((r: any) => 
                      `${r.name}: ${r.value} ${r.unit}`
                    ).join(' | ') || '',
                  }),
                ],
                spacing: { after: 100 },
              })
            ),
          ],
        }],
      });

      const blob = await Packer.toBlob(doc);
      saveAs(blob, `Special_Monitoring_${student.surname}_${format(new Date(), 'yyyy-MM-dd')}.docx`);

      toast({
        title: "Success",
        description: "Report generated successfully",
      });

      onOpenChange(false);
    } catch (error) {
      console.error('Error generating report:', error);
      toast({
        title: "Error",
        description: "Failed to generate report",
        variant: "destructive",
      });
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Generate Report</DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          <div className="text-sm text-muted-foreground">
            This will generate a comprehensive Word document report including:
          </div>
          <ul className="text-sm space-y-2 list-disc list-inside">
            <li>Student information</li>
            <li>Summary statistics for all parameters</li>
            <li>Reading history with timestamps</li>
            <li>Alert summary</li>
            <li>Trend analysis</li>
          </ul>

          <div className="flex justify-end gap-2 pt-4">
            <Button variant="outline" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button onClick={generateDocument}>
              <Download className="h-4 w-4 mr-2" />
              Generate & Download
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
